import {
  Calendar,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  EyeOff,
  OctagonX,
  RefreshCw,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { type EntityMap, useEntitiesByIds } from "@/hooks/api/use-entities";
import {
  type MilestoneAnalysis,
  type MilestoneAnalysisOtherCitation,
  type MilestoneAnalysisTouchpoint,
  type TaskGroup,
  useRetryMilestoneAnalysisAnalysis,
  useUpdateMilestoneAnalysisStatus,
  useUpdateMilestoneAnalysisTask,
} from "@/hooks/api/use-milestone-analyses";
import { useMilestoneAnalysisAdminEnabled } from "@/hooks/auth/use-permissions-gates";
import { formatDate } from "@/lib/date-formatting";
import { HistoryDialog } from "./history-dialog";

interface MilestoneAnalysisCardProps {
  taskGroup: TaskGroup;
  onFilterCitedTouchpoints?: (taskId: string, touchpointIds: string[]) => void;
}

interface TouchpointTableProps {
  touchpoints: MilestoneAnalysisTouchpoint[];
}

function TouchpointTable({ touchpoints }: TouchpointTableProps) {
  return (
    <div className="mt-4 border rounded-md">
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="px-3 py-2 text-left text-xs font-medium text-muted-foreground w-[10px] whitespace-nowrap">
              Date
            </TableHead>
            <TableHead className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              Activity
            </TableHead>
            <TableHead className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              Reason
            </TableHead>
            <TableHead className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              Detail
            </TableHead>
            <TableHead className="px-3 py-2 text-left text-xs font-medium text-muted-foreground w-[120px] whitespace-nowrap">
              Channel
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {touchpoints.map((touchpoint) => (
            <TableRow key={touchpoint.id} className="border-t">
              <TableCell className="px-3 py-2 text-sm whitespace-nowrap">
                {formatDate(touchpoint.date)}
              </TableCell>
              <TableCell className="px-3 py-2 text-sm">
                {touchpoint.inferred_from_id
                  ? touchpoint.detail
                  : touchpoint.activity}
              </TableCell>
              <TableCell className="px-3 py-2 text-sm">
                {touchpoint.reason || ""}
              </TableCell>
              <TableCell className="px-3 py-2 text-sm">
                {touchpoint.inferred_from_id ? (
                  <div>
                    <Badge
                      variant="secondary"
                      className="mr-2 mb-1 text-xs bg-supporting-purple/10 text-supporting-purple hover:bg-supporting-purple/10"
                    >
                      INFERRED FROM
                    </Badge>
                    <span className="text-sm">{touchpoint.activity}</span>
                  </div>
                ) : (
                  touchpoint.detail
                )}
              </TableCell>
              <TableCell className="px-3 py-2 text-sm whitespace-nowrap">
                <Badge className="text-xs">{touchpoint.channel}</Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface OtherCitationsTableProps {
  citations: MilestoneAnalysisOtherCitation[];
  entitiesMap?: EntityMap;
  isLoading?: boolean;
}

function OtherCitationsTable({
  citations,
  entitiesMap = {},
  isLoading = false,
}: OtherCitationsTableProps) {
  // Function to get entity display name
  const getEntityDisplay = (entityId: string) => {
    if (isLoading) return "Loading...";

    const entity = entitiesMap?.[entityId];
    if (!entity) return entityId;

    return entity.name || entityId;
  };

  return (
    <div className="mt-4 border rounded-md">
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              Entity
            </TableHead>
            <TableHead className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              Field
            </TableHead>
            <TableHead className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              Detail
            </TableHead>
            <TableHead className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              Reason
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {citations.map((item) => (
            <TableRow key={`${item.id}-${item.field}`} className="border-t">
              <TableCell className="px-3 py-2 text-sm">
                <div className="flex flex-col gap-1">
                  {entitiesMap?.[item.id] && (
                    <Badge
                      variant="secondary"
                      className="text-xs inline-flex w-fit"
                    >
                      {entitiesMap[item.id].type}
                    </Badge>
                  )}
                  {getEntityDisplay(item.id)}
                </div>
              </TableCell>
              <TableCell className="px-3 py-2 text-sm">{item.field}</TableCell>
              <TableCell className="px-3 py-2 text-sm">{item.detail}</TableCell>
              <TableCell className="px-3 py-2 text-sm">{item.reason}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Helper function to get the analysis to display
const getAnalysisToDisplay = (
  analyses: MilestoneAnalysis[],
  selectedId: string | null,
): MilestoneAnalysis | undefined => {
  if (!analyses || analyses.length === 0) {
    return undefined;
  }
  const sortedAnalyses = [...analyses].sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
  );
  const activeAnalysis = sortedAnalyses.find((a) => a.is_active_version);
  const userSelectedAnalysis = selectedId
    ? sortedAnalyses.find((a) => a.analysis_id === selectedId)
    : null;

  return userSelectedAnalysis || activeAnalysis || sortedAnalyses[0];
};

/**
 * MilestoneAnalysisCard component for displaying milestone analysis content
 */
export function MilestoneAnalysisCard({
  taskGroup,
  onFilterCitedTouchpoints,
}: MilestoneAnalysisCardProps) {
  // Sort analyses by last_updated, newest first (used for history dialog)
  const sortedAnalyses = useMemo(
    () =>
      [...taskGroup.analyses].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
      ),
    [taskGroup.analyses],
  );

  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(
    null,
  );
  const [showTouchpoints, setShowTouchpoints] = useState(true);
  const [showOtherCitations, setShowOtherCitations] = useState(true);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [instructions, setInstructions] = useState("");
  const isAdmin = useMilestoneAnalysisAdminEnabled();
  const posthog = usePostHog();

  // Determine the analysis to display based on state and props
  const analysisToDisplay = useMemo(
    () => getAnalysisToDisplay(taskGroup.analyses, selectedAnalysisId),
    [taskGroup.analyses, selectedAnalysisId],
  );

  // Convert metadata evidence for display
  const displayMetadataEvidence = useMemo(
    () => analysisToDisplay?.other_citations || [],
    [analysisToDisplay],
  );

  // Extract entity IDs from metadata evidence
  const entityIds = useMemo(() => {
    return displayMetadataEvidence.map((item) => item.id).filter(Boolean);
  }, [displayMetadataEvidence]);

  // Fetch entities data on component load
  const { data: entitiesMap, isLoading: isLoadingEntities } =
    useEntitiesByIds(entityIds);

  const regenerateMutation = useUpdateMilestoneAnalysisTask();
  const updateStatusMutation = useUpdateMilestoneAnalysisStatus();
  const retryMutation = useRetryMilestoneAnalysisAnalysis();

  // If no analysis could be determined (edge case, e.g., empty task group), render nothing
  if (!analysisToDisplay) {
    return null;
  }

  // For non-completed analyses, we don't show any content
  // This includes processing and pending states
  // Failed state is handled separately to show error details
  // Only show content for completed analyses with actual content or failed analyses
  if (
    taskGroup.processing_status !== "failed" && // Allow failed tasks to be displayed
    (!analysisToDisplay.summary ||
      taskGroup.processing_status === "processing" ||
      taskGroup.processing_status === "pending")
  ) {
    return null;
  }

  // For non-admin users, hide unapproved analyses entirely
  if (!isAdmin && !analysisToDisplay.is_approved) {
    return null;
  }

  const handleRegenerate = async () => {
    try {
      await regenerateMutation.mutateAsync(
        {
          task_id: taskGroup.task_id,
          extra_instructions: instructions,
        },
        {
          onSuccess: () => {
            posthog?.capture("analysis_task_regenerated", {
              analysis_type: taskGroup.analysis_type,
              task_id: taskGroup.task_id,
              opportunity_id: taskGroup.opportunity_id,
              has_extra_instructions: !!instructions,
            });
          },
        },
      );

      // After regenerating, we likely want to view the new analysis
      // Resetting selectedId will make it default to the (new) active one
      setSelectedAnalysisId(null);
      setShowRegenerateDialog(false);
      setInstructions("");
    } catch (error) {
      console.error("Failed to regenerate analysis:", error);
    }
  };

  // Get touchpoints for display
  const displayTouchpoints = analysisToDisplay.touchpoint_citations || [];

  // Handle viewing a specific analysis (called from History Dialog)
  const handleViewAnalysis = (analysis: MilestoneAnalysis) => {
    setSelectedAnalysisId(analysis.analysis_id);
  };

  // Handle analysis change (e.g., publish) from History Dialog
  const handleAnalysisChangeFromDialog = (analysis: MilestoneAnalysis) => {
    // When an analysis is changed (published) in the dialog, ensure we view it.
    setSelectedAnalysisId(analysis.analysis_id);
  };

  // Handle filter touchpoints
  const handleFilterTouchpoints = () => {
    const touchpointIds = displayTouchpoints.map((tp) => tp.id);
    if (onFilterCitedTouchpoints && touchpointIds.length > 0) {
      onFilterCitedTouchpoints(taskGroup.task_id, touchpointIds);
    }
  };

  return (
    <div className="overflow-hidden border-muted">
      <div className="p-4 m-4 bg-muted/30 rounded-lg shadow-sm">
        {/* First row: Milestone type and date */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Milestone type badge */}
            <Badge variant="opportunity-active" className="text-xs">
              {taskGroup.processing_status === "failed"
                ? taskGroup.analysis_type // For failed tasks, use the task group's property
                : analysisToDisplay.analysis_type}
            </Badge>

            {/* Date badge */}
            <div className="flex items-center text-sm text-muted-foreground">
              <Calendar className="h-4 w-4 mr-1" />
              {formatDate(analysisToDisplay.opportunity_created_date || "")}
            </div>
          </div>

          {/* Admin controls for analyses */}
          {isAdmin && (
            <div className="flex gap-1 items-center">
              {sortedAnalyses.length > 1 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setShowHistoryDialog(true)}
                      >
                        <Clock className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View previous analyses ({sortedAnalyses.length})</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {taskGroup.processing_status !== "failed" && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setShowRegenerateDialog(true)}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Regenerate analysis</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {taskGroup.processing_status === "failed" && (
                <div className="flex items-center p-1 bg-destructive/10 rounded border border-destructive overflow-hidden">
                  <div className="flex items-center px-2 py-1">
                    <OctagonX className="h-3 w-3 mr-1 text-destructive" />
                    <span className="text-xs text-destructive">Failed</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 py-1 rounded-none border-l border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => {
                      retryMutation.mutate(
                        {
                          task_id: taskGroup.task_id,
                        },
                        {
                          onSuccess: () => {
                            posthog?.capture("analysis_task_retried", {
                              analysis_type: taskGroup.analysis_type,
                              task_id: taskGroup.task_id,
                              opportunity_id: taskGroup.opportunity_id,
                              error_message: taskGroup.error_message?.slice(
                                0,
                                100,
                              ), // Send just the beginning of the error
                            });
                          },
                        },
                      );
                    }}
                    disabled={retryMutation.isPending}
                  >
                    {retryMutation.isPending ? "Retrying..." : "Retry"}
                  </Button>
                </div>
              )}
              {/* Version indicators - only show for analyses, not for failed tasks */}
              {taskGroup.processing_status !== "failed" &&
                (!analysisToDisplay.is_active_version ? (
                  <div className="flex items-center p-1 bg-warning/10 rounded border border-warning overflow-hidden">
                    <div className="flex items-center px-2 py-1">
                      <EyeOff className="h-3 w-3 mr-1 text-warning" />
                      <span className="text-xs text-warning">Unpublished</span>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 py-1 rounded-none border-l border-warning text-warning hover:bg-warning/10 hover:text-warning"
                            onClick={() => {
                              updateStatusMutation.mutate(
                                {
                                  analysisId: analysisToDisplay.analysis_id,
                                  isApproved: true,
                                  isActiveVersion: true,
                                },
                                {
                                  onSuccess: (updatedAnalysis) => {
                                    // Ensure we are viewing the newly published analysis
                                    setSelectedAnalysisId(
                                      updatedAnalysis.analysis_id,
                                    );
                                    posthog?.capture("analysis_published", {
                                      analysis_id:
                                        analysisToDisplay.analysis_id,
                                      analysis_type:
                                        analysisToDisplay.analysis_type,
                                      opportunity_id: taskGroup.opportunity_id,
                                      task_id: taskGroup.task_id,
                                    });
                                  },
                                },
                              );
                            }}
                            disabled={updateStatusMutation.isPending}
                          >
                            {updateStatusMutation.isPending &&
                            updateStatusMutation.variables?.analysisId ===
                              analysisToDisplay.analysis_id &&
                            updateStatusMutation.variables?.isActiveVersion ===
                              true
                              ? "Publishing..."
                              : "Publish"}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Make this the currently active version</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                ) : (
                  <div className="flex items-center p-1 bg-success/10 rounded border border-success overflow-hidden gap-2">
                    <div className="flex items-center px-2 py-1">
                      <Check className="h-3 w-3 mr-1 text-success" />
                      <span className="text-xs text-success">Published</span>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 py-1 rounded-none border-l border-success text-success hover:bg-success/10 hover:text-success"
                            onClick={() => {
                              updateStatusMutation.mutate(
                                {
                                  analysisId: analysisToDisplay.analysis_id,
                                  isApproved: true,
                                  isActiveVersion: false,
                                },
                                {
                                  onSuccess: () => {
                                    posthog?.capture("analysis_unpublished", {
                                      analysis_id:
                                        analysisToDisplay.analysis_id,
                                      analysis_type:
                                        analysisToDisplay.analysis_type,
                                      opportunity_id: taskGroup.opportunity_id,
                                      task_id: taskGroup.task_id,
                                    });
                                  },
                                },
                              );
                            }}
                            disabled={updateStatusMutation.isPending}
                          >
                            Unpublish
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Unpublish this version</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Second row: Channel badges */}
        {(analysisToDisplay?.primary_touchpoint_channel ||
          analysisToDisplay?.assist_touchpoint_channel) && (
          <div className="flex items-center gap-1.5 mt-2">
            {analysisToDisplay?.primary_touchpoint_channel && (
              <Badge variant="default" className="text-xs">
                {analysisToDisplay?.primary_touchpoint_channel}
              </Badge>
            )}
            {analysisToDisplay?.assist_touchpoint_channel && (
              <Badge variant="secondary" className="text-xs">
                {analysisToDisplay?.assist_touchpoint_channel}
              </Badge>
            )}
          </div>
        )}

        {/* Analysis Content */}
        <div className="mt-4">
          <div className="mb-4">
            {analysisToDisplay.opinion?.opinion && (
              <h3 className="text-md font-semibold mb-2">
                {analysisToDisplay.opinion.opinion}
              </h3>
            )}
            <p className="text-sm text-muted-foreground">
              {analysisToDisplay.summary}
            </p>
          </div>
        </div>

        {/* Touchpoints Section */}
        {displayTouchpoints.length > 0 && (
          <div className="mt-2">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTouchpoints(!showTouchpoints)}
              >
                <span>Cited touchpoints ({displayTouchpoints.length})</span>
                {showTouchpoints ? (
                  <ChevronUp className="ml-1 h-4 w-4" />
                ) : (
                  <ChevronDown className="ml-1 h-4 w-4" />
                )}
              </Button>
              {displayTouchpoints.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={handleFilterTouchpoints}
                >
                  Highlight in story
                </Button>
              )}
            </div>
            {showTouchpoints && (
              <TouchpointTable touchpoints={displayTouchpoints} />
            )}
          </div>
        )}

        {/* Other Citations Section */}
        {analysisToDisplay.other_citations &&
          analysisToDisplay.other_citations.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowOtherCitations(!showOtherCitations)}
                >
                  <span>
                    Other citations ({analysisToDisplay.other_citations.length})
                  </span>
                  {showOtherCitations ? (
                    <ChevronUp className="ml-1 h-4 w-4" />
                  ) : (
                    <ChevronDown className="ml-1 h-4 w-4" />
                  )}
                </Button>
              </div>
              {showOtherCitations && (
                <OtherCitationsTable
                  citations={analysisToDisplay.other_citations}
                  entitiesMap={entitiesMap}
                  isLoading={isLoadingEntities}
                />
              )}
            </div>
          )}
      </div>

      {/* Dialogs */}
      <HistoryDialog
        open={showHistoryDialog}
        onOpenChange={setShowHistoryDialog}
        sortedAnalyses={sortedAnalyses}
        onViewAnalysis={handleViewAnalysis}
        onAnalysisChange={handleAnalysisChangeFromDialog}
      />

      <Dialog
        open={showRegenerateDialog}
        onOpenChange={setShowRegenerateDialog}
      >
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Regenerate this summary</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="(Optional) Provide additional instructions for the agent to consider."
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowRegenerateDialog(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRegenerate}
              disabled={regenerateMutation.isPending}
            >
              {regenerateMutation.isPending ? "Regenerating..." : "Regenerate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
